import { useLiveStreamHoursAvailable } from "@circle-react/components/LiveStreams/hooks";
import { useStreamHoursAddon } from "@circle-react/components/LiveStreams/hooks";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { noop } from "lodash";
import { createContext, useContext, useEffect, useState } from "react";

interface AddonsContextProps {
  limitsReachedIsOpen?: boolean;
  limitsReachedOnClose: () => void;
  limitsReachedOnOpen: () => void;
  purchaseStreamingAddonWhenLimitReached: () => Promise<void>;
  shouldUpgradeAddons?: boolean;
  streamingHoursAvailable: boolean;
  totalStreamingHours: number;
}

const AddonsContext = createContext<AddonsContextProps>({
  streamingHoursAvailable: false,
  totalStreamingHours: 0,
  limitsReachedIsOpen: false,
  limitsReachedOnClose: noop,
  limitsReachedOnOpen: noop,
  shouldUpgradeAddons: false,
  purchaseStreamingAddonWhenLimitReached: () => Promise.resolve(),
});
AddonsContext.displayName = "AddonsContext";

export const AddonsProvider = ({ children }: any) => {
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const {
    streamingHoursAvailable: hasStreamingHoursAvailable,
    streamingTotalPlanHours,
    isLoading: isLoadingUsage,
  } = useLiveStreamHoursAvailable({ enabled: isAdmin });

  const {
    purchaseAsync: purchaseStreamingAddon,
    currentQuantity: streamHoursAddonQuantity,
    unitSize: streamingAddonUnitSize,
  } = useStreamHoursAddon(noop, { enabled: isAdmin });

  const totalStreamingHours =
    streamingTotalPlanHours + Number(streamHoursAddonQuantity || 0);

  const [isLimitsReachedIsOpen, setIsLimitsReachedIsOpen] = useState<
    boolean | undefined
  >(false);

  useEffect(() => {
    setIsLimitsReachedIsOpen(
      isAdmin && !isLoadingUsage && !hasStreamingHoursAvailable,
    );
  }, [isAdmin, isLoadingUsage, hasStreamingHoursAvailable]);

  const limitsReachedOnClose = () => {
    setIsLimitsReachedIsOpen(false);
  };

  const limitsReachedOnOpen = () => {
    setIsLimitsReachedIsOpen(true);
  };

  const shouldUpgradeAddons = !hasStreamingHoursAvailable;

  const purchaseStreamingAddonWhenLimitReached = async () => {
    if (hasStreamingHoursAvailable) return;
    if (!streamingAddonUnitSize) return;

    await purchaseStreamingAddon(streamingAddonUnitSize);
  };

  const value: AddonsContextProps = {
    streamingHoursAvailable: hasStreamingHoursAvailable,
    totalStreamingHours,
    limitsReachedIsOpen: isLimitsReachedIsOpen,
    limitsReachedOnClose,
    limitsReachedOnOpen,
    shouldUpgradeAddons,
    purchaseStreamingAddonWhenLimitReached,
  };

  return (
    <AddonsContext.Provider value={value}>{children}</AddonsContext.Provider>
  );
};

export const useAddonsContext = (): AddonsContextProps => {
  const context = useContext(AddonsContext);

  // eslint-disable-next-line sonarjs/different-types-comparison -- Disabled for plugin version update. Fix when possible.
  if (context === undefined) {
    throw new Error("useAddonsContext must be used within a AddonsProvider");
  }

  return context;
};
